// Shared band derivation for the PCB stator's printed inductive encoder.
// THE single source of sensor-track radii: the coil generator, target ring,
// rotor recess, and encoder board assembly all import this module so track
// misalignment (a silently-dead sensor) is structurally impossible.
// No Angular / DI deps. Spec amendment: 2026-07-09 (target band inboard of
// the magnet pockets — through-pockets leave no back-face material outboard).

import { RotorLayout, RECESS_FIT_MM } from './pcb-stator-rotor';
import { DISC_RIM_MM } from './pcb-stator-encoder-target';

export interface Band { rInMm: number; rOutMm: number; }

export interface EncoderBands {
  coarse: Band;             // inner track
  fine: Band;               // outer track (larger r = better accuracy)
  tx: Band;                 // TX allocation. TX copper occupies ONLY the outward
                            // wrap (outside fine); rInMm is a legacy inner
                            // reference — derive real copper extents from
                            // SensingElement.traces, never from bands.tx.
  hubKeepAwayMm: number;    // min radius for any sensor copper (rotating hub metal rule)
  valid: boolean;           // false when geometry can't fit (drives red banner)
  reasons: string[];        // human-readable failures for the banner
}

export const TRACK_GAP_MM = 1.5;         // between coarse and fine bands
export const POCKET_CLEAR_MM = 1.5;      // recess/track clearance to magnet pockets
export const HUB_KEEP_AWAY_MM = 3;       // beyond hub/bore hardware radius
const TX_WRAP_MM = 0.6;                  // TX inner-band margin inside coarse (hub floor is tight: keep <= 0.6)
// Outward-only TX allocation. The TX excitation is a nested planar SPIRAL of a
// few concentric turns living entirely OUTSIDE the fine RX band (so no TX copper
// ever enters an RX annulus on the shared 'down' layer — that would be a galvanic
// short). The chip is placed radially beyond this in Task 7, so growing the ring
// outward here is allowed.
//
// SIZING (2026-07-12 fix — TX window at the shipped fixture): the wrap must hold
// enough TX turns for the Wheeler current-sheet estimate (see estimateTxInductanceNH)
// to clear the chip's LC-tank window FLOOR (1675 nH) at the SHIPPED trace geometry
// (0.3 mm trace + 0.3 mm clearance ⇒ 0.6 mm winding pitch), not the 0.2/0.2 the
// Task-4 sizing wrongly assumed. buildTxLoop caps the turn count at what fits the
// wrap. maxTurns ≥ 4 needs floor((wrap − 0.75)/0.6) ≥ 3 ⇒ wrap ≥ 2.55 mm, so the
// wrap is sized for N=4: 2.7 mm gives maxTurns = 4 (rCapCenter − rStart = 1.95 mm
// ⇒ 4 turns at 0.6 mm pitch). This requirement depends only on the trace/clearance/
// via-pad geometry, NOT the absolute band radius, so it is UNCHANGED by the seat
// shave below (2026-07-13): shaving fine.rOutMm slides the whole TX spiral inward,
// which cuts its diameters and drops the Wheeler estimate from N=4→2293 nH (at the
// pre-shave fine.rOut R25.9) to N=4→~2174 nH (at the shaved R24.75) — still well
// above the 1675 nH floor and inside the [1675, 12665] nH window, so no wrap
// widening is needed. The old N=5-vs-N=4 key-finger argument is MOOT now that the
// disc OD is decoupled from bands.tx.rOutMm (the disc no longer underlies the TX
// loop — see DISC_RIM_MM in pcb-stator-encoder-target and the seat shave below).
const TX_OUTER_WRAP_MM = 2.7;
const MIN_ANNULUS_MM = 8;                // below this radial width, nothing usable fits

const ZERO_BAND: Band = { rInMm: 0, rOutMm: 0 };

/** Derives the coarse/fine sensor track annuli and the enclosing TX loop
 *  radii from the rotor's pocket geometry and the stator hub radius. The
 *  available annulus sits strictly inboard of the magnet pockets — through-
 *  pockets leave solid back-face disc material only there. */
export function computeEncoderBands(rotor: RotorLayout, hubRadiusMm: number): EncoderBands {
  const hubKeepAwayMm = hubRadiusMm + HUB_KEEP_AWAY_MM;
  const reasons: string[] = [];

  if (!rotor.valid) {
    reasons.push(`Rotor layout is invalid: ${rotor.warning ?? 'unknown reason'}.`);
  }

  const availStart = hubKeepAwayMm;
  // The outer track's copper edge (fine.rOutMm, which equals availEnd below) must
  // leave room OUTBOARD of itself for the physical target-seat stack, so the passive
  // target disc can seat FULLY in the rotor's back-face counterbore rather than
  // resting proud (2026-07-13 seating fix). Two reserves stack outboard of fine
  // copper: (1) DISC_RIM_MM — the disc extends a solid substrate rim beyond the fine
  // RX lobe tracks (eddy-current targets need substrate only under the RX lobes, NOT
  // under the TX loop, so the disc OD is fine.rOutMm + rim, decoupled from tx.rOutMm);
  // (2) RECESS_FIT_MM — the disc drops into the counterbore with a slip-fit gap to
  // its wall. The counterbore's outer wall is itself pocket-clamped at
  // pocketInner − POCKET_CLEAR (it must never break into a through-pocket), so the
  // seat closes iff (fine.rOut + DISC_RIM + RECESS_FIT) ≤ that wall. Shaving the
  // annulus end by the reserve makes that hold with equality — the largest fine.rOut
  // (best RX accuracy) that still seats. Parametric: pocketInner tracks pole count /
  // magnet size, so the cap follows any rotor automatically.
  const seatReserveMm = DISC_RIM_MM + RECESS_FIT_MM;
  const availEnd = rotor.pocketInnerRadiusMm - POCKET_CLEAR_MM - seatReserveMm;
  const width = availEnd - availStart;

  if (width < MIN_ANNULUS_MM) {
    reasons.push(
      `Available sensing annulus is ${width.toFixed(2)}mm (need >= ${MIN_ANNULUS_MM}mm): hub keep-away out ` +
      `to R${availStart.toFixed(2)}mm, and the target-seat reserve (disc rim + fit) pulls the outer edge in to ` +
      `R${availEnd.toFixed(2)}mm off the pocket-clamped recess wall at R${(rotor.pocketInnerRadiusMm - POCKET_CLEAR_MM).toFixed(2)}mm.`,
    );
  }

  if (reasons.length > 0) {
    return { coarse: ZERO_BAND, fine: ZERO_BAND, tx: ZERO_BAND, hubKeepAwayMm, valid: false, reasons };
  }

  // Split: coarse gets the inner third, fine the outer two-thirds minus the
  // gap between them — the two widths plus the gap exactly fill `width`.
  const coarseWidth = width / 3;
  const fineWidth = (2 * width) / 3 - TRACK_GAP_MM;

  const coarse: Band = { rInMm: availStart, rOutMm: availStart + coarseWidth };
  const fine: Band = {
    rInMm: coarse.rOutMm + TRACK_GAP_MM,
    rOutMm: coarse.rOutMm + TRACK_GAP_MM + fineWidth,
  };
  // TX band: nominal inner reference just inside coarse, and an outward-only
  // spiral allocation outside fine (the excitation turns live here — see
  // TX_OUTER_WRAP_MM). Inner wrap stays <= 0.6 mm (hub floor is at coarse.rIn).
  const tx: Band = { rInMm: coarse.rInMm - TX_WRAP_MM, rOutMm: fine.rOutMm + TX_OUTER_WRAP_MM };

  return { coarse, fine, tx, hubKeepAwayMm, valid: true, reasons };
}
